import React, { useState } from "react";
import {
    useGetWeeklyPercentStatsQuery,
    useGetStrategiesQuery,
    InvestmentWeeklyStat,
} from "../api";

interface InvestmentWeeklyStatsProps {
    skip?: boolean;
}

export const InvestmentWeeklyStats: React.FC<InvestmentWeeklyStatsProps> = ({
    skip = false,
}) => {
    const [strategyFilter, setStrategyFilter] = useState<number | undefined>(
        undefined
    );
    const [dateFrom, setDateFrom] = useState<string>("");
    const [dateTo, setDateTo] = useState<string>("");

    const { data, isLoading, refetch } = useGetWeeklyPercentStatsQuery(
        {
            strategy: strategyFilter,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            limit: 100,
        },
        { skip }
    );

    const { data: strategiesResponse } = useGetStrategiesQuery();
    const strategies = strategiesResponse?.data || [];

    const getStrategyName = (id: number) => {
        const strategy = strategies.find(s => s.id === id);
        return strategy ? `${id}: ${strategy.name}` : `${id}: Стратегия ${id}`;
    };

    const stats = data?.data?.stats || [];
    const summary = data?.data?.summary || {
        total_sum: 0,
        count: 0,
    };

    return (
        <div>
            <div className="flex justify-end items-center mb-4">
                <button
                    onClick={() => refetch()}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
                >
                    Обновить
                </button>
            </div>

            {/* Фильтры */}
            <div className="mb-4 flex gap-4 flex-wrap">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Стратегия
                    </label>
                    <select
                        value={strategyFilter || ""}
                        onChange={(e) =>
                            setStrategyFilter(
                                e.target.value
                                    ? parseInt(e.target.value)
                                    : undefined
                            )
                        }
                        className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                        <option value="">Все</option>
                        {strategies.map((s) => (
                            <option key={s.id} value={s.id}>
                                {s.id}: {s.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Дата от
                    </label>
                    <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Дата до
                    </label>
                    <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                </div>
            </div>

            {/* Сводка */}
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            Всего начислено:
                        </span>
                        <span className="ml-2 font-bold text-gray-900 dark:text-white">
                            ${summary.total_sum.toFixed(2)}
                        </span>
                    </div>
                    <div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            Количество записей:
                        </span>
                        <span className="ml-2 font-bold text-gray-900 dark:text-white">
                            {summary.count}
                        </span>
                    </div>
                </div>
            </div>

            {/* Таблица */}
            {isLoading ? (
                <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                    Загрузка...
                </div>
            ) : stats.length === 0 ? (
                <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                    Нет данных
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Дата
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Стратегия
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Сумма
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {stats.map((stat: InvestmentWeeklyStat) => (
                                <tr key={stat.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        {stat.date}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        {getStrategyName(stat.strategy)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-white">
                                        ${stat.sum.toFixed(2)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
